import { Command } from 'common/Prose/Commands'
import { TextSelection } from 'prosemirror-state'
import { Fragment, Slice } from 'prosemirror-model'

/**
 * If you are in the summary and press enter (not shift+enter), then move the selection into a new toggle item
 * and put your cursor there
 */
export const splitToggleListItem: Command = ({ doc, schema, selection: { $from, $to, empty }, tr }, dispatch, view) => {
  if (!empty && $from.node() !== $to.node()) return false
  if (!$from.node(-1) || $from.node(-1).type !== schema.nodes.summary) return false

  // We are inside the summary.

  // Remove the toggle if empty.
  if ($from.node(-2).nodeSize <= 8) {
    tr.delete($from.before(-2), $from.after(-2))
    tr.setSelection(TextSelection.create(tr.doc, $from.before(-2) + 1))
    dispatch?.(tr)
    return true
  }

  // If collapsed, create a new toggle list item.
  const toggleDomNode = view?.domAtPos($from.start(-2))
  const isExpanded =
    toggleDomNode != null &&
    toggleDomNode.node instanceof HTMLElement &&
    toggleDomNode.node.parentElement?.getAttribute('aria-expanded') === 'true'
  if (!isExpanded) {
    const toggleStart = $from.start(-2)
    const toggleNode = $from.node(-2)
    const summaryNode = $from.node(-1)

    // The part of the summary before the cursor goes into the first toggle. Also give it the toggle contents (all children after the first child).
    let t1 = toggleNode.cut(0, $from.pos - toggleStart)
    t1 = t1.copy(t1.content.append(Fragment.from(toggleNode.content.content.slice(1))))

    // Rest of the summary (after the cursor) goes into the second toggle. Give it an empty paragraph as toggle content.
    let t2 = toggleNode.cut($from.pos - toggleStart, summaryNode.nodeSize)
    t2 = t2.copy(t2.content.append(Fragment.from(schema.nodes.paragraph.create())))

    tr.replace($from.before(-2), $from.after(-2), new Slice(Fragment.fromArray([t1, t2]), 0, 0))
    tr.setSelection(TextSelection.create(tr.doc, toggleStart + t1.nodeSize + 2))
    dispatch?.(tr)
    return true
  }

  // If cursor is at the end, just move the selection to the body.
  const isCursorAtTheEndOfSummary = $from.pos + 1 === $from.end(-1)
  if (isCursorAtTheEndOfSummary) {
    tr.setSelection(TextSelection.create(doc, $from.pos + 3))
    dispatch?.(tr)
    return true
  }

  // Otherwise split at the cursor and move the rest of the summary into the body.
  const toggleSize = $from.node(-2).nodeSize
  const summarySize = $from.node(-1).nodeSize
  const hasEmptyBody = toggleSize - summarySize <= 4
  tr.split($from.pos, 1)
  const splitNodes = tr.doc.slice(tr.selection.$from.before(), tr.selection.$from.after(-1) - 1)
  if (hasEmptyBody) {
    tr.replace(tr.selection.$from.after(-1), tr.selection.$from.after(-1) + 2, splitNodes)
  } else {
    tr.insert(tr.selection.$from.after(-1), splitNodes.content)
  }
  tr.delete(tr.selection.$from.before(), tr.selection.$from.after(-1))
  dispatch?.(tr)
  return true
}
